import React, { useEffect, useRef, useState } from 'react';
import { Menu } from 'lucide-react';
import Brand from './nav/Brand';
import SidebarNav from './nav/SidebarNav';
import BottomTabs from './nav/BottomTabs';
import { ProxyBanner } from './ProxyBanner';
import { useProxy } from '../contexts/ProxyContext';
interface AppShellProps {
  children: React.ReactNode;
}

const MOBILE_HEADER_HEIGHT = 56; // h-14
const PROXY_BANNER_HEIGHT = 25;

// AppShell: sticky glass header with scroll elevation + responsive nav.
export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [elevated, setElevated] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const { isProxyActive } = useProxy();

  useEffect(() => {
    const onScroll = () => setElevated(window.scrollY > 0);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const safeAreaTop = 'env(safe-area-inset-top, 0px)';
  const bannerHeight = isProxyActive ? PROXY_BANNER_HEIGHT : 0;
  const shellStyle: React.CSSProperties = {
    '--mobile-header-offset': `calc(${bannerHeight}px + ${safeAreaTop})`,
    '--mobile-content-offset': `calc(${bannerHeight + MOBILE_HEADER_HEIGHT}px + ${safeAreaTop})`,
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900" style={shellStyle}>
      {/* Proxy Banner */}
      <ProxyBanner />

      {/* Persistent sidebar on desktop */}
      <SidebarNav mode="static" />

      {/* Sticky glass header for mobile/tablet */}
      <div
        ref={headerRef}
        className={`lg:hidden fixed left-0 right-0 z-40 glass ${elevated ? 'e2' : 'e1'} border-b border-gray-200/60 dark:border-gray-800/60`}
        style={{ top: 'var(--mobile-header-offset)' }}
      >
        <div className="h-14 flex items-center px-4">
          <button
            ref={menuButtonRef}
            aria-label="Open navigation"
            aria-haspopup="dialog"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
            className="mr-3 inline-flex items-center justify-center rounded-md p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 flex items-center justify-center">
            <Brand size="md" />
          </div>
          <div className="ml-2 flex items-center gap-2" />
        </div>
      </div>

      {/* Drawer sidebar for mobile/tablet */}
      <SidebarNav
        mode="drawer"
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        triggerRef={menuButtonRef}
      />

      {/* Main content */}
      <main className="flex-1 overflow-hidden bg-gray-50 dark:bg-gray-900">
        <div className="h-full overflow-y-auto pt-[var(--mobile-content-offset)] lg:pt-[calc(25px+env(safe-area-inset-top,0px))] pb-16 md:pb-0 [padding-bottom:env(safe-area-inset-bottom,0px)]">
          {children}
        </div>
      </main>

      {/* Bottom tabs on mobile */}
      <BottomTabs />
    </div>
  );
};

export default AppShell;
